import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MetaTags from 'react-meta-tags';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';

import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { normalizePhones, loadActivatedAccounts, getFormValue } from 'utils/';
import { ufToState, numberTag, stateOptions, FILE_SIZE_5MB } from '../../constants/index';

import SuccessModal from '../../components/Success-Modal';
import ErrorModal from '../../components/Error-Modal';
import Breadcrumbs from '../../components/Breadcrumb';
import LabelGroup from '../../components/Label-Group';
import TextInput from '../../components/Text-Input';
import TextAreaInput from '../../components/TextArea-Input';
import SelectInput from '../../components/Select-Input';
import DateInput from '../../components/Date-Input';
import SwitchInput from '../../components/Switch-Input';
import PhonesInput from '../../components/Phones-Input';
import SubmitButton from '../../components/Submit-Button';
import '../../assets/scss/custom/pages/_users.scss';

const EditUser = () => {
  const formConfig = {
    defaultValues: {
      accountType: [],
      status: '',
      oldStatus: '',
      account: [],
      unchangedEmail: '',
      email: '',
      name: '',
      lastName: '',
      tasks: true,
      content: false,
      occupation: '',
      description: '',
      birthdate: '',
      phones: [{ tag: '', number: '' }],
      zipCode: '',
      addressLine1: '',
      addressLine2: '',
      state: [],
      city: '',
      emailNotification: {
        newTask: false,
        assignedToTask: false,
        newComment: false,
        newMeeting: false,
      },
    },
    shouldFocusError: true,
    criteriaMode: 'all',
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  };
  const firebaseHelper = getFirebaseBackend();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const userParam = searchParams.get('id');
  const form = useForm(formConfig);
  const { formState, getValues, watch } = form;
  const userAccount = getValues('fixedAccount');
  const { t: translate } = useTranslation();
  const { user, updateUser } = useAuth();
  const superAdminStatus = user.adminStatus === 'Super Admin';

  const [accountModules, setAccountModules] = useState(null);
  const [file, setFile] = useState(null);
  const [updateSuccessfull, setUpdateSuccessfull] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [changedFile, setChangedFile] = useState(false);
  const [hasExistingPhoto, setHasExistingPhoto] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const onSubmit = async (data) => {
    try {
      setIsSending(true);

      const userAccounts = data.account.map(getFormValue);
      const loggedAccount = userAccounts.includes(data.fixedAccount)
        ? data.fixedAccount
        : userAccounts[0];

      const photoUrl = changedFile
        ? await firebaseHelper.updateUserPhoto(file, userParam)
        : data.photoUrl ?? null;

      const user = {
        id: userParam,
        status: data.oldStatus,
        account: loggedAccount,
        accounts: userAccounts,
        email: data.email,
        name: data.name,
        lastName: data.lastName,
        adminStatus: data.accountType.value,
        hasPhoto: Boolean(file) || hasExistingPhoto,
        photoUrl,
        modules: {
          seo: data.seo,
          tasks: data.tasks,
          meetings: data.meetings,
          content: data.content,
          courses: data.courses,
          keywordReport: data['keywordReport'],
          paidMedia: data['paidMedia'],
          contentNew: data.content,
        },
        occupation: data.occupation ? data.occupation : null,
        description: data.description ? data.description : null,
        birthDate: data.birthdate ? Date.parse(data.birthdate) : null,
        phones: normalizePhones(data.phones),
        zipCode: data.zipCode ? data.zipCode : null,
        addressLine1: data.addressLine1 ? data.addressLine1 : null,
        addressLine2: data.addressLine2 ? data.addressLine2 : null,
        state: data?.state?.value ? data.state.value : null,
        city: data.city ? data.city : null,
        emailNotification: data.emailNotification ?? {
          newTask: false,
          assignedToTask: false,
          newComment: false,
          newMeeting: false,
        },
        createdAt: data.createdAt,
      };

      await firebaseHelper.updateUser(user);

      if (data.status.value === 'Activated') {
        await firebaseHelper.activateUser(user.id);
      }
      if (data.status.value === 'Deactivated') {
        await firebaseHelper.deactivateUser(user.id);

        const firebaseToken = await firebaseHelper.getIdToken();

        await axios.post(
          `${process.env.REACT_APP_API_URL}/removeUserFromTasks`,
          {
            userId: user.id,
            development: process.env.REACT_APP_BUILD_TYPE !== 'production',
          },
          {
            headers: {
              Authorization: `Bearer ${firebaseToken}`,
            },
          },
        );
      }
      setSuccessModal(true);
      updateUser();
      setIsSending(false);
    } catch (error) {
      console.error(error);
      setErrorModal(true);
      setIsSending(false);
    }
  };

  const closeModalHandle = () => {
    setFile(null);
    setSuccessModal(false);
    getUser();
  };

  const handleFileSubmit = (photoFile) => {
    const loader = document.getElementById('loading-icon');
    const ghost = document.getElementById('ghost-icon');
    const image = document.getElementById('file-display');

    // Checks if a file was uploaded and if it matches the valid extensions and puts it as source for the img tag
    if (photoFile) {
      if (
        !(
          photoFile.type === 'image/png' ||
          photoFile.type === 'image/jpg' ||
          photoFile.type === 'image/jpeg'
        )
      ) {
        alert(
          translate('The profile photo file extesion needs to be one of the following') +
            ': png, jpg, jpeg.',
        );
        document.getElementById('file-input').value = null;
      } else if (photoFile.size > FILE_SIZE_5MB) {
        alert(translate('Max size of file is 5MB'));
        document.getElementById('file-input').value = null;
      } else {
        setChangedFile(true);
        setFile(photoFile);
        image.style.display = 'inline';
        image.src = URL.createObjectURL(photoFile);
        ghost.style.display = 'none';
        loader.style.display = 'none';
      }
    } else {
      setFile(null);
      ghost.style.display = 'inline';
      image.style.display = 'none';
      loader.style.display = 'none';
    }
  };

  const activateLoader = () => {
    document.getElementById('loading-icon').style.display = 'inline';
    document.getElementById('ghost-icon').style.display = 'none';
    document.getElementById('file-display').style.display = 'none';
  };

  const showExistingPhoto = (photoUrl) => {
    const loader = document.getElementById('loading-icon');
    const ghost = document.getElementById('ghost-icon');
    const image = document.getElementById('file-display');

    setHasExistingPhoto(true);
    image.style.display = 'inline';
    image.src = photoUrl;
    ghost.style.display = 'none';
    loader.style.display = 'none';
  };

  const getAccountModules = async () => {
    const account = await firebaseHelper.getAccount(userAccount);
    const modules = account.modules;

    setAccountModules({ ...modules });
  };

  const getUser = async () => {
    try {
      const user = await firebaseHelper.getUser(userParam);

      let processedAccount;

      if (user.accounts) {
        processedAccount = await Promise.all(
          user.accounts.map(async (account) => ({
            value: account,
            label: await firebaseHelper.accountName(account),
          })),
        );
      } else {
        processedAccount = [
          {
            value: user.account,
            label: await firebaseHelper.accountName(user.account),
          },
        ];
      }

      const processedPhones = user.phones
        ? user.phones.map(({ tag, number }) => ({
            number,
            tag: {
              value: tag,
              label: translate(numberTag[tag]),
            },
          }))
        : [{ tag: '', number: '' }];

      form.reset({
        accountType: {
          value: user.adminStatus,
          label: translate(user.adminStatus),
        },
        fixedAccount: user.account,
        account: processedAccount,
        email: user.email,
        name: user.name,
        lastName: user.lastName,
        seo: user.modules?.seo === true,
        tasks: user.modules?.tasks === true,
        meetings: user.modules?.tasks === true && user.modules?.meetings === true ? true : false,
        content: user.modules?.content === true,
        contentNew: user.modules?.content === true,
        courses: user.modules?.courses === true,
        keywordReport: user.modules?.keywordReport === true,
        paidMedia: user.modules?.paidMedia === true,
        occupation: user?.occupation,
        description: user?.description,
        birthdate: user.birthDate ? new Date(user.birthDate) : '',
        zipCode: user?.zipCode,
        addressLine1: user?.addressLine1,
        addressLine2: user?.addressLine2,
        state: user.state ? { value: user.state, label: ufToState[user.state] } : null,
        city: user?.city,
        phones: processedPhones,
        oldStatus: user.status,
        status: { value: user.status, label: translate(user.status) },
        hasPhoto: user.hasPhoto,
        photoUrl: user.photoUrl,
        emailNotification: user.emailNotification ?? {
          newTask: false,
          assignedToTask: false,
          newComment: false,
          newMeeting: false,
        },
        createdAt: user.createdAt,
      });

      if (user.photoUrl) {
        activateLoader();
        showExistingPhoto(user.photoUrl);
        setChangedFile(false);
      }
      setUpdateSuccessfull(true);
    } catch (error) {
      console.error(error);
      setErrorModal(true);
    }
  };

  const handleRemoveFile = (event) => {
    event.preventDefault();

    setFile(null);
    setHasExistingPhoto(false);
    handleFileSubmit(null);
    setChangedFile(true);

    document.querySelector('#file-input').value = null;
  };

  useEffect(() => {
    const initialize = async () => {
      await getUser();
    };
    initialize();
  }, []);

  useEffect(() => {
    if (!userAccount) return;

    getAccountModules();
  }, [userAccount]);

  useEffect(() => {
    const currentRole = watch('accountType.value');

    if (currentRole === 'Client' || currentRole === 'Client Admin') {
      getAccountModules();
    }
  }, [watch('accountType')]);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{translate('Edit User')} | Ectools</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title={translate('Clients')} breadcrumbItem={translate('Edit')} />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <SuccessModal
                    isOpen={successModal}
                    onClose={closeModalHandle}
                    verb="Updated"
                    buttonText="See Users"
                    buttonLink="/list-users"
                  />

                  <ErrorModal isOpen={errorModal} />

                  <CardTitle className="mb-2 fw-normal" style={{ fontSize: '1.01rem' }}>
                    {translate('Edit Existing User')}
                  </CardTitle>

                  <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{ width: '49%' }}>
                          <LabelGroup htmlFor="status" label="Status" mb={1} column required>
                            <SelectInput
                              controlName="status"
                              required
                              options={[
                                {
                                  value: 'Activated',
                                  label: translate('Activated'),
                                },
                                {
                                  value: 'Deactivated',
                                  label: translate('Deactivated'),
                                },
                              ]}
                            />
                          </LabelGroup>

                          {superAdminStatus && (
                            <>
                              <LabelGroup
                                htmlFor="accountType"
                                label="Account Type"
                                mb={1}
                                column
                                required
                              >
                                <SelectInput
                                  controlName="accountType"
                                  required
                                  options={[
                                    { value: 'Client', label: 'Client' },
                                    {
                                      value: 'Client Admin',
                                      label: 'Client Admin',
                                    },
                                    { value: 'Admin', label: 'Admin' },
                                    {
                                      value: 'Super Admin',
                                      label: 'Super Admin',
                                    },
                                  ]}
                                />
                              </LabelGroup>

                              <LabelGroup htmlFor="account" label="Account" mb={1} column required>
                                <SelectInput
                                  asyncronous
                                  isMulti
                                  controlName="account"
                                  required
                                  cacheOptions
                                  defaultOptions
                                  loadOptions={(inputValue) => loadActivatedAccounts(inputValue)}
                                />
                              </LabelGroup>
                            </>
                          )}

                          <LabelGroup htmlFor="email" label="Email" mb={1} column required>
                            <TextInput
                              controlName="email"
                              placeholder="Email"
                              required
                              disabled
                              validation={{
                                pattern: {
                                  value: RegExp(/^\S+@\S+.+\S+$/),
                                  message: 'Invalid Email!',
                                },
                              }}
                            />
                          </LabelGroup>

                          <LabelGroup htmlFor="name" label="FirstName" mb={1} column required>
                            <TextInput controlName="name" placeholder="FirstName" required />
                          </LabelGroup>
                          <LabelGroup htmlFor="lastName" label="LastName" mb={1} column required>
                            <TextInput controlName="lastName" placeholder="LastName" required />
                          </LabelGroup>

                          <FormGroup className="mb-1 mt-3" row>
                            <Col lg="2">
                              <div
                                style={{
                                  height: '6rem',
                                  width: '6rem',
                                  border: '1px solid #CED4DA',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  borderRadius: '4px',
                                }}
                              >
                                <i
                                  id="ghost-icon"
                                  className="bx bx-ghost"
                                  style={{ fontSize: '4rem', color: '#D2D2D2' }}
                                />

                                <i
                                  id="loading-icon"
                                  className="bx bx-loader-alt loading-animation"
                                  style={{
                                    fontSize: '2rem',
                                    color: '#D2D2D2',
                                    display: 'none',
                                  }}
                                />

                                <img
                                  id="file-display"
                                  width="90%"
                                  style={
                                    file === null && !hasExistingPhoto
                                      ? { display: 'none' }
                                      : { display: 'inline', maxHeight: '90%' }
                                  }
                                />
                              </div>
                            </Col>
                            <Col lg="9">
                              <Col
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Label
                                  style={{
                                    fontSize: '13px',
                                    height: '26px',
                                    color: '#495057',
                                    fontWeight: '400',
                                  }}
                                >
                                  {translate('Profile Photo')}
                                </Label>
                                {(Boolean(file) || hasExistingPhoto) && (
                                  <button
                                    className="remove-file-btn"
                                    onClick={(e) => handleRemoveFile(e)}
                                  >
                                    <box-icon
                                      type="regular"
                                      name="trash"
                                      color="#F46A6A"
                                      size="1.3rem"
                                    />
                                    <span>{translate('Remove Image')}</span>
                                  </button>
                                )}
                              </Col>
                              <Input
                                id="file-input"
                                className="form-control"
                                type="file"
                                onChange={(event) => handleFileSubmit(event.target.files[0])}
                                accept="image/png, image/jpg, image/jpeg"
                              />
                              <p className="upload-photo-default-caption">
                                {translate('Upload Photo Default Caption')}
                              </p>
                            </Col>
                          </FormGroup>

                          {Boolean(watch('accountType.value')) &&
                            Boolean(watch('account')?.length) && (
                              <LabelGroup htmlFor="tasks" label="Access Modules" mb={1} column>
                                {accountModules?.tasks && (
                                  <SwitchInput controlName="tasks" label="Tasks" />
                                )}
                                {watch('tasks') === true && (
                                  <SwitchInput controlName="meetings" label="Meetings" />
                                )}
                                {(watch('accountType.value') === 'Admin' ||
                                  watch('accountType.value') === 'Super Admin') &&
                                  accountModules?.content === true && (
                                    <SwitchInput controlName="content" label="Content" />
                                  )}
                                {/* {accountModules?.seo && (
                                  <SwitchInput controlName="seo" label="SEO" />
                                )} */}
                                {/* {accountModules?.paidMedia && (
                                  <SwitchInput controlName="paidMedia" label="PaidMedia" />
                                )} */}
                                {accountModules?.keywordReport && (
                                  <SwitchInput controlName="keywordReport" label="KeywordReport" />
                                )}
                                {accountModules?.courses && (
                                  <SwitchInput controlName="courses" label="Courses" />
                                )}
                              </LabelGroup>
                            )}
                        </div>

                        <div
                          style={{
                            height: 'auto',
                            width: '1px',
                            backgroundColor: '#E2E2E2',
                          }}
                        />

                        <div style={{ width: '49%' }}>
                          <LabelGroup htmlFor="occupation" label="Occupation" mb={1} column>
                            <TextInput controlName="occupation" placeholder="Occupation" />
                          </LabelGroup>

                          <LabelGroup htmlFor="description" label="Description" mb={1} column>
                            <TextAreaInput
                              controlName="description"
                              placeholder="Enter the description"
                              size={12}
                              mb={2}
                              manualResize
                            />
                          </LabelGroup>

                          <LabelGroup htmlFor="birthdate" label="Birthdate" mb={1} column>
                            <DateInput controlName="birthdate" placeholder="Birthdate" />
                          </LabelGroup>

                          <PhonesInput mb={1} />

                          <LabelGroup htmlFor="zipCode" label="Zip Code" mb={1} column>
                            <TextInput
                              controlName="zipCode"
                              placeholder="Zip Code"
                              mask="99999-999"
                              maskChar=" "
                            />
                          </LabelGroup>

                          <LabelGroup htmlFor="addressLine1" label="Address Line 1" mb={1} column>
                            <TextInput controlName="addressLine1" placeholder="Address Line 1" />
                          </LabelGroup>

                          <LabelGroup htmlFor="addressLine2" label="Address Line 2" mb={1} column>
                            <TextInput controlName="addressLine2" placeholder="Address Line 2" />
                          </LabelGroup>

                          <LabelGroup htmlFor="state" label="State and City" mb={1} column>
                            <Row>
                              <SelectInput size={3} controlName="state" options={stateOptions} />
                              <TextInput size={9} controlName="city" placeholder="City" />
                            </Row>
                          </LabelGroup>

                          <LabelGroup
                            htmlFor="emailNotification"
                            label="Email Notifications"
                            mb={1}
                            column
                          >
                            {/* <SwitchInput
                              controlName="emailNotification.newTask"
                              label="New task created on the project"
                            />
                            <SwitchInput
                              controlName="emailNotification.assignedToTask"
                              label="Tasks assigned to me"
                            /> */}
                            <SwitchInput
                              controlName="emailNotification.newComment"
                              label="New comments on tasks"
                            />
                            <SwitchInput
                              controlName="emailNotification.newMeeting"
                              label="New meeting created on the project"
                            />
                          </LabelGroup>
                        </div>
                      </div>

                      <SubmitButton
                        text="Edit User"
                        justifyLeft
                        disabled={Boolean(
                          Object.values(formState.errors).length || !updateSuccessfull,
                        )}
                        isLoading={isSending}
                      />
                    </form>
                  </FormProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

EditUser.propTypes = {
  location: PropTypes.object,
  search: PropTypes.string,
};

export default EditUser;
